import * as React from 'react';
import { z } from 'zod/v4';
import {
  DynamicForm,
  Input,
  TextArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  MultipleSelector,
  type Option,
} from '@org/ui';
import { Loader2 } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BroadcastAudience = 'all' | 'groups' | 'users';

/**
 * Values emitted on submit, ready for `notifications.sendBroadcast`.
 * `targetIds` holds group ids or user ids depending on `audience` ([] for all).
 */
export type NotificationBroadcastFormValues = {
  title: string;
  body: string;
  link: string | null;
  audience: BroadcastAudience;
  targetIds: string[];
  /** Human labels of the picked targets, for the confirmation text. */
  targetLabels: string[];
};

export type NotificationBroadcastFormProps = {
  onSubmit: (values: NotificationBroadcastFormValues) => void;
  onClose: () => void;
  isSubmitting?: boolean;
  /** Async search for groups (3+ chars, debounced by MultipleSelector). */
  onSearchGroups?: (query: string) => Promise<Option[]>;
  /** Async search for students (3+ chars, debounced by MultipleSelector). */
  onSearchUsers?: (query: string) => Promise<Option[]>;
  /** Shown under the audience pickers when groups/students is chosen but nothing is picked. */
  needTargetsMessage?: string;
  /** Form id so a button outside the form (modal footer) can submit it. */
  formRef?: React.RefObject<HTMLFormElement>;
  labels?: {
    title?: string;
    body?: string;
    link?: string;
    linkHelp?: string;
    audience?: string;
    audienceAll?: string;
    audienceGroups?: string;
    audienceUsers?: string;
    groups?: string;
    users?: string;
    groupSearchPlaceholder?: string;
    userSearchPlaceholder?: string;
    noGroups?: string;
    noUsers?: string;
  };
};

// ---------------------------------------------------------------------------
// Zod schema (used internally by DynamicForm). Limits mirror the server.
// ---------------------------------------------------------------------------

const broadcastSchema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(120),
  body: z.string().trim().min(1, 'Message is required').max(500),
  link: z
    .string()
    .trim()
    .max(500)
    .refine((v) => v === '' || v.startsWith('/') || v.startsWith('https://'), {
      message: 'Use a page like /conquistas or a full https:// address',
    })
    .default(''),
  audience: z.enum(['all', 'groups', 'users']).default('all'),
  // Stored as Option[] inside the form (MultipleSelector), converted on submit.
  groupIds: z.array(z.any()).default([]),
  userIds: z.array(z.any()).default([]),
});

type SchemaValues = z.infer<typeof broadcastSchema>;

type RendererContext = {
  field: { value: unknown; onChange: (value: unknown) => void };
  error?: string;
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function AsyncMultiSelect({
  value,
  onChange,
  onSearch,
  placeholder,
  emptyLabel,
}: {
  value: Option[];
  onChange: (options: Option[]) => void;
  onSearch?: (query: string) => Promise<Option[]>;
  placeholder: string;
  emptyLabel: string;
}) {
  return (
    <MultipleSelector
      value={value}
      onChange={onChange}
      onSearch={onSearch}
      delay={400}
      placeholder={placeholder}
      emptyIndicator={<p className="text-center text-sm text-muted-foreground">{emptyLabel}</p>}
      loadingIndicator={
        <div className="flex items-center justify-center py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      }
      hidePlaceholderWhenSelected
    />
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function NotificationBroadcastForm({
  onSubmit,
  onClose,
  isSubmitting = false,
  onSearchGroups,
  onSearchUsers,
  needTargetsMessage,
  formRef: externalFormRef,
  labels,
}: NotificationBroadcastFormProps) {
  const internalFormRef = React.useRef<HTMLFormElement>(null);
  const formRef = externalFormRef ?? internalFormRef;
  const [targetsError, setTargetsError] = React.useState<string | null>(null);

  const dynamicDefaultValues = React.useMemo<Partial<SchemaValues>>(
    () => ({ title: '', body: '', link: '', audience: 'all', groupIds: [], userIds: [] }),
    [],
  );

  const handleSubmit = async (data: SchemaValues) => {
    const picked =
      data.audience === 'groups'
        ? (data.groupIds as unknown as Option[])
        : data.audience === 'users'
          ? (data.userIds as unknown as Option[])
          : [];
    const targetIds = picked.map((o) => o.value).filter(Boolean);

    if (data.audience !== 'all' && targetIds.length === 0) {
      setTargetsError(needTargetsMessage ?? 'Pick at least one group or student first.');
      return;
    }
    setTargetsError(null);

    const link = data.link.trim();
    onSubmit({
      title: data.title.trim(),
      body: data.body.trim(),
      link: link ? link : null,
      audience: data.audience,
      targetIds,
      targetLabels: picked.map((o) => o.label),
    });
  };

  const customRenderers: Record<string, (context: RendererContext) => React.ReactNode> = {
    body: ({ field }) => (
      <TextArea
        value={(field.value ?? '') as string}
        onChange={field.onChange}
        className="min-h-[120px]"
        maxLength={500}
        disabled={isSubmitting}
      />
    ),

    link: ({ field }) => (
      <div className="space-y-1">
        <Input
          value={(field.value ?? '') as string}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.onChange(e.target.value)}
          placeholder="/conquistas"
          disabled={isSubmitting}
        />
        <p className="text-xs text-muted-foreground">
          {labels?.linkHelp ?? 'A page in the student app, e.g. /conquistas, or a full https:// address.'}
        </p>
      </div>
    ),

    audience: ({ field }) => (
      <Select
        value={field.value as string}
        onValueChange={(v) => {
          setTargetsError(null);
          field.onChange(v);
        }}
      >
        <SelectTrigger>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{labels?.audienceAll ?? 'All students'}</SelectItem>
          <SelectItem value="groups">{labels?.audienceGroups ?? 'Specific groups'}</SelectItem>
          <SelectItem value="users">{labels?.audienceUsers ?? 'Specific students'}</SelectItem>
        </SelectContent>
      </Select>
    ),

    groupIds: ({ field }) => (
      <div className="space-y-1">
        <AsyncMultiSelect
          value={(field.value ?? []) as Option[]}
          onChange={(options) => {
            setTargetsError(null);
            field.onChange(options);
          }}
          onSearch={onSearchGroups}
          placeholder={labels?.groupSearchPlaceholder ?? 'Search groups (3+ chars)...'}
          emptyLabel={labels?.noGroups ?? 'No groups found'}
        />
        {targetsError && <p className="text-xs text-destructive">{targetsError}</p>}
      </div>
    ),

    userIds: ({ field }) => (
      <div className="space-y-1">
        <AsyncMultiSelect
          value={(field.value ?? []) as Option[]}
          onChange={(options) => {
            setTargetsError(null);
            field.onChange(options);
          }}
          onSearch={onSearchUsers}
          placeholder={labels?.userSearchPlaceholder ?? 'Search students (3+ chars)...'}
          emptyLabel={labels?.noUsers ?? 'No students found'}
        />
        {targetsError && <p className="text-xs text-destructive">{targetsError}</p>}
      </div>
    ),
  };

  const fieldVisibility = React.useCallback(
    ({ key, values }: { key: string; values: SchemaValues; isEditing: boolean }): boolean => {
      if (key === 'groupIds') return values.audience === 'groups';
      if (key === 'userIds') return values.audience === 'users';
      return true;
    },
    [],
  );

  const labelMappings: Record<string, string> = {
    title: labels?.title ?? 'Title',
    body: labels?.body ?? 'Message',
    link: labels?.link ?? 'Link (optional)',
    audience: labels?.audience ?? 'Audience',
    groupIds: labels?.groups ?? 'Groups',
    userIds: labels?.users ?? 'Students',
  };

  return (
    <DynamicForm
      schema={broadcastSchema}
      onSubmit={handleSubmit}
      onClose={onClose}
      defaultValues={dynamicDefaultValues}
      formRef={formRef}
      labelMappings={labelMappings}
      fieldVisibility={fieldVisibility}
      customRenderers={customRenderers as any}
      hideButtons
    />
  );
}
